import React, { useContext } from 'react';
import { toast } from 'react-toastify';
import {
  FormContext,
  FormContextProvider,
  FormSchema,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import FormModal from '../../Shared/form/modal/FormModal';
import inspectionReviewApi from '../../../utils/api/inspectionReview';

interface Props {
  show: boolean;
  reviewId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const schema: FormSchema = {
  reason: {
    label: 'Reason',
    controlType: UIControlType.textarea,
    validation: { required: true },
  },
};

/**
 * SW2-2248 — the reviewer's "send back for revision" flow. Mirrors
 * `RequestApprovalModal.tsx`'s shape (the closest existing analog: a single-field `FormModal`
 * posting to a request/response API) rather than `ConfirmationModal`, since the backend requires
 * a non-empty, trimmed reason (`InspectionReviewController.reject` 400s without one).
 */
const RejectReviewModalUI: React.FC<Props> = ({
  show,
  reviewId,
  onClose,
  onSuccess,
}) => {
  const formContext = useContext(FormContext);

  const handleSubmit = (formData: any) => {
    if (!reviewId) return Promise.resolve();

    const reason = (formData.reason ?? '').trim();

    return inspectionReviewApi
      .reject(reviewId, reason)
      .then(() => {
        toast.success('Inspection sent back for revision');
        onSuccess();
        onClose();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Failed to send the inspection back for revision',
        );
        throw err;
      });
  };

  if (!show) return null;

  return (
    <FormModal
      modalProps={{ title: 'Send Back for Revision' }}
      onSubmit={() => handleSubmit(formContext.value)}
      onCancel={onClose}
    >
      <FormSchemaFields
        schema={schema}
        formData={formContext.value}
        onChange={formContext.set}
      />
    </FormModal>
  );
};

const RejectReviewModal: React.FC<Props> = (props) => {
  if (!props.show) return null;

  return (
    <FormContextProvider initialValue={{}}>
      <RejectReviewModalUI {...props} />
    </FormContextProvider>
  );
};

export default RejectReviewModal;
